<script setup lang="ts">
const emit = defineEmits(["close", "closeParentPopover"]);
const props = defineProps({
  mode: {
    type: String as PropType<"desktop" | "mobile">,
    required: true,
  },
});
const { getUserBalance } = useUserController();
const userStore = useUserStore();
const {
  pageSize,
  isFetching,
  fetchNotifications,
  fetchMore,
  readSelected,
  readAll,
} = useNotifications();
const infiniteScroll = ref<HTMLElement | null>(null);

const checkScroll = () => {
  if (infiniteScroll.value) {
    const { scrollTop, scrollHeight, clientHeight } = infiniteScroll.value;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 20;
    if (atBottom) {
      fetchMore();
    }
  }
};

const closeMenus = () => {
  emit("close");
  if (props.mode === "mobile") emit("closeParentPopover");
};

const init = async () => {
  pageSize.value = props.mode === "mobile" ? 20 : 6;
  getUserBalance();
  await fetchNotifications();
  onNuxtReady(async () => {
    if (infiniteScroll.value) {
      infiniteScroll.value.addEventListener("scroll", checkScroll);
    }
  });
};

init();
</script>

<template>
  <div>
    <div class="relative w-full hidden md:block">
      <div
        class="absolute right-3.5 transform rotate-180 translate-y-[-0.05rem] bottom-full w-4 border-solid border-secondary-500 border-t-8 border-x-transparent border-x-8 border-b-0" />
      <div
        class="absolute right-3.5 transform rotate-180 translate-y-[0.05rem] bottom-full w-4 border-solid border-neutral-800 border-t-8 border-x-transparent border-x-8 border-b-0" />
    </div>
    <div
      class="flex items-center justify-between py-10 md:py-3 px-4 border-b-neutral-400 md:border-b-secondary-500 border-b max-h-[105px]">
      <p class="text-neutral-50 font-bold text-xl md:text-base">
        {{ $t("main.headerNotifications.notifications") }}
      </p>
      <NuxtIcon
        v-if="props.mode === 'mobile'"
        name="mdi:close"
        :alt="$t('main.imageAltTexts.closeIcon')"
        class="cursor-pointer w-6 h-6 text-neutral-300"
        @click="emit('close')" />
    </div>
    <div
      v-if="userStore.notifications.unread > 0"
      class="md:hidden flex items-center cursor-pointer px-4 py-4"
      @click="readAll">
      <NuxtIcon
        name="mdi:check-all"
        :alt="$t('main.headerNotifications.markAsRead')"
        class="w-4 h-4 text-primary-500" />
      <p class="text-primary-500 ml-2">
        {{ $t("main.headerNotifications.markAsRead") }}
      </p>
    </div>
    <div
      ref="infiniteScroll"
      class="notification-scrollbar overflow-y-scroll overscroll-none md:max-h-96 min-h-30 mb-2"
      :class="{
        'h-[81vh]': mode === 'mobile',
      }">
      <div
        v-if="userStore.notifications.records.length === 0 && !isFetching"
        class="flex flex-col items-center text-center py-4">
        <NuxtIcon name="mdi:bell" class="w-12 h-12 text-neutral-300" />
        <p class="font-bold text-neutral-50">
          {{ $t("main.headerNotifications.noNotifications") }}
        </p>
        <p class="text-neutral-400 max-w-[219px]">
          {{ $t("main.headerNotifications.notificationsShowHere") }}
        </p>
      </div>
      <template
        v-for="notification in userStore.notifications.records"
        v-else
        :key="notification.id">
        <HeaderAccountNotificationsItem
          :notification="notification"
          :readSelected="readSelected"
          :closeMenus="closeMenus" />
      </template>
      <div
        v-if="isFetching"
        class="flex flex-col items-center text-center py-4">
        <NuxtIcon
          name="svg-spinners:ring-resize"
          :alt="$t('main.headerNotifications.notifications')"
          width="48"
          class="mt-2 mb-8" />
      </div>
      <div
        v-if="
          userStore.notifications.records.length ===
            userStore.notifications.total &&
          !isFetching &&
          userStore.notifications.records.length > 0
        "
        class="flex flex-col items-center text-center py-4">
        <BaseIconFlag class="mt-4 w-7 h-7" />
        <p class="text-neutral-400 mt-2 mb-6 max-w-[219px]">
          {{ $t("main.headerNotifications.lastNotifications") }}
        </p>
      </div>
    </div>
    <transition
      enter-active-class="transition-opacity duration-800 ease-in-out"
      leave-active-class="transition-opacity duration-800 ease-in-out"
      enter-class="opacity-0"
      leave-to-class="opacity-0">
      <div
        v-if="userStore.notifications.unread > 0"
        class="border-t-secondary-500 border-t absolute bg-neutral-800 bottom-0 w-full border-secondary-500 md:rounded-b-lg">
        <div class="md:block hidden mx-4 my-2">
          <div
            class="hidden md:flex items-center cursor-pointer"
            @click="readAll">
            <NuxtIcon
              name="mdi:check-all"
              :alt="$t('main.headerNotifications.markAsRead')"
              class="w-4 h-4 text-primary-500" />
            <p class="text-primary-500 ml-2">
              {{ $t("main.headerNotifications.markAsRead") }}
            </p>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.notification-scrollbar::-webkit-scrollbar {
  width: 4px;
  height: 8px;
  background-color: transparent !important;
}

.notification-scrollbar::-webkit-scrollbar-track {
  border-radius: 1rem;
  margin: 0.4rem;
  margin-top: 0.4rem;
  border-left: 1px solid rgba(211, 211, 211, 0);
}

.notification-scrollbar::-webkit-scrollbar-thumb {
  border-radius: 20px;
  background: #b5b5b5;
  box-shadow: none;
}

.notification-scrollbarinput::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
